import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DemoWidget from '../components/DemoWidget';
import { WORKERS } from '../workerConfig';
import { fetchJson } from '../api';

// ── Turnstile Demo ────────────────────────────────────────────────
function TurnstileDemo() {
  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        Turnstile is Cloudflare's privacy-preserving CAPTCHA alternative. It verifies humans without showing puzzles.
        Try both demos below — one with a real human, one simulating a bot.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
        <a 
          href="https://turnstile.shruti-test.com/good/" 
          target="_blank" 
          rel="noreferrer"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '1.5rem',
            background: 'var(--cf-success-light)',
            border: '2px solid var(--cf-success)',
            borderRadius: 'var(--radius)',
            textDecoration: 'none',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,165,80,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</span>
          <strong style={{ color: 'var(--cf-success)', fontSize: '1rem' }}>Human Flow</strong>
          <span style={{ fontSize: '0.8rem', color: 'var(--cf-muted)', marginTop: '0.25rem' }}>
            Real Turnstile widget
          </span>
        </a>

        <a 
          href="https://turnstile.shruti-test.com/bad/" 
          target="_blank" 
          rel="noreferrer"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '1.5rem',
            background: 'var(--cf-danger-light)',
            border: '2px solid var(--cf-danger)',
            borderRadius: 'var(--radius)',
            textDecoration: 'none',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(239,68,68,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✗</span>
          <strong style={{ color: 'var(--cf-danger)', fontSize: '1rem' }}>Bot Challenge</strong>
          <span style={{ fontSize: '0.8rem', color: 'var(--cf-muted)', marginTop: '0.25rem' }}>
            See what bots see
          </span>
        </a>
      </div>

      <div className="result-box result-info">
        <strong>How Turnstile Works:</strong>
        <ul style={{ margin: '0.5rem 0 0 1rem', fontSize: '0.85rem', lineHeight: '1.6' }}>
          <li>No visual puzzles — uses browser signals and ML</li>
          <li>Privacy-preserving — no tracking cookies</li>
          <li>Free for unlimited use</li>
          <li>Works in visible or invisible mode</li>
        </ul>
      </div>
    </div>
  );
}

// ── Bot Score Demo (Real) ─────────────────────────────────────────
function BotScoreDemo() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    score: number | null;
    verifiedBot: boolean;
    userAgent: string;
    ip: string;
    country: string;
    asn: number;
    asOrganization: string;
    colo: string;
    tlsVersion: string;
    httpProtocol: string;
    note: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchBotScore = React.useCallback(async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const data = await fetchJson<typeof result>(`${WORKERS.securityDemo}/bot-score`);
      setResult(data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch bot score');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBotScore();
  }, [fetchBotScore]);

  // Detect if traffic is coming through WARP/Cloudflare network
  const isWarpTraffic = result?.asn === 13335 || result?.asOrganization?.toLowerCase().includes('cloudflare');

  // Cloudflare Bot Score: 1 = bot, 99 = human
  const getScoreColor = (score: number | null) => {
    if (score === null) return 'var(--cf-muted)';
    if (score >= 70) return 'var(--cf-success)';  // High score = human
    if (score >= 30) return 'var(--cf-warning)';  // Mid score = suspicious
    return 'var(--cf-danger)';                     // Low score = bot
  };

  const getScoreLabel = (score: number | null) => {
    if (score === null) return 'N/A';
    if (score >= 70) return 'Likely Human';
    if (score >= 30) return 'Suspicious';
    return 'Likely Bot';
  };

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        This shows the <strong>real bot score</strong> for your current request. Cloudflare analyzes browser signals, 
        TLS fingerprint, and behavioral data to score each request 1-99 (1 = bot, 99 = human).
      </p>

      <button className="btn btn-primary" onClick={fetchBotScore} disabled={loading} style={{ marginBottom: '1rem' }}>
        {loading ? <><span className="spinner" /> Fetching...</> : 'Refresh My Bot Score'}
      </button>

      {error && <div className="result-box result-error">{error}</div>}

      {result && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '1rem', alignItems: 'start' }}>
            {/* Bot Score Card */}
            <div style={{
              width: '120px',
              padding: '1.25rem',
              background: `${getScoreColor(result.score)}15`,
              border: `2px solid ${getScoreColor(result.score)}`,
              borderRadius: 'var(--radius)',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 900, color: getScoreColor(result.score), lineHeight: 1 }}>
                {result.score ?? '—'}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--cf-muted)', marginTop: '0.25rem', textTransform: 'uppercase', fontWeight: 700 }}>
                Bot Score
              </div>
              <div style={{ 
                fontSize: '0.75rem', 
                fontWeight: 700, 
                color: getScoreColor(result.score),
                marginTop: '0.5rem',
                padding: '0.2rem 0.5rem',
                background: `${getScoreColor(result.score)}20`,
                borderRadius: '4px',
              }}>
                {getScoreLabel(result.score)}
              </div>
              {result.verifiedBot && (
                <div style={{ 
                  fontSize: '0.7rem', 
                  fontWeight: 700, 
                  color: 'var(--cf-blue)',
                  marginTop: '0.5rem',
                }}>
                  ✓ Verified Bot
                </div>
              )}
            </div>

            {/* Request Details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div className="metric-row">
                <span className="metric-label">Your IP</span>
                <code style={{ fontSize: '0.8rem' }}>{result.ip}</code>
              </div>
              <div className="metric-row">
                <span className="metric-label">Country</span>
                <span className="metric-value">{result.country}</span>
              </div>
              <div className="metric-row">
                <span className="metric-label">ASN</span>
                <span className="metric-value">{result.asn} ({result.asOrganization})</span>
              </div>
              <div className="metric-row">
                <span className="metric-label">Edge Location</span>
                <span className="metric-value">{result.colo}</span>
              </div>
              <div className="metric-row">
                <span className="metric-label">TLS Version</span>
                <span className="metric-value">{result.tlsVersion}</span>
              </div>
              <div className="metric-row">
                <span className="metric-label">Protocol</span>
                <span className="metric-value">{result.httpProtocol}</span>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--cf-bg)', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem' }}>
            <strong>User-Agent:</strong>
            <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', marginTop: '0.25rem', wordBreak: 'break-all', color: 'var(--cf-muted)' }}>
              {result.userAgent}
            </div>
          </div>

          {result.score === null && (
            <div className="result-box result-warning" style={{ marginTop: '1rem' }}>
              <strong>Note:</strong> {result.note}
            </div>
          )}

          {isWarpTraffic && (
            <div className="result-box result-info" style={{ marginTop: '1rem' }}>
              <strong>Cloudflare Network Detected</strong>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
                Your traffic is routing through Cloudflare's network (ASN 13335), likely via WARP.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── WAF Inspection Demo ───────────────────────────────────────────
function WAFDemo() {
  const [payload, setPayload] = useState('');
  const [path, setPath] = useState('/api/users?id=1');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{
    blocked: boolean;
    action: string;
    triggeredRules: Array<{ rule: string; category: string; matched: string }>;
    message: string;
    realBlock?: boolean;
    rayId?: string;
  } | null>(null);

  const encodeBytes = (value: string) => Array.from(new TextEncoder().encode(value));

  const check = async () => {
    setChecking(true);
    setResult(null);
    try {
      const data = await fetchJson<{
        verified: boolean;
        blocked: boolean;
        action: string;
        message: string;
        rayId?: string;
        triggeredRules?: Array<{ rule: string; category: string; matched: string }>;
      }>(`${WORKERS.securityDemo}/waf-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payload: encodeBytes(payload), path: encodeBytes(path) }),
      });
      setResult({ ...data, realBlock: data.verified, triggeredRules: data.triggeredRules ?? [] });
    } catch (error) {
      setResult({
        blocked: false,
        action: 'ERROR',
        triggeredRules: [],
        message: error instanceof Error ? error.message : 'Unable to verify the WAF response',
      });
    } finally {
      setChecking(false);
    }
  };

  const presets = [
    { label: 'SQLi', payload: "' OR 1=1--", path: '/api/users?id=1' },
    { label: 'XSS', payload: '<script>alert(1)</script>', path: '/search?q=' },
    { label: 'Path Traversal', payload: '', path: '/../../../etc/passwd' },
    { label: 'RCE', payload: '; cat /etc/passwd', path: '/api/exec?cmd=' },
    { label: 'Clean', payload: '', path: '/api/users?id=123' },
  ];

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        Send common attack patterns through a Worker-based inspector. This demonstrates detection logic but does not
        claim that a Cloudflare managed WAF rule fired; verified Security Events integration is still to be built.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {presets.map((p) => (
          <button 
            key={p.label} 
            className="btn btn-secondary btn-sm" 
            onClick={() => { setPayload(p.payload); setPath(p.path); setResult(null); }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem', marginBottom: '1rem' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Request Path</label>
          <input className="form-input" value={path} onChange={(e) => setPath(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Payload</label>
          <input className="form-input" value={payload} onChange={(e) => setPayload(e.target.value)} placeholder="Attack payload..." />
        </div>
      </div>

      <button className="btn btn-primary" onClick={check} disabled={checking}>
        {checking ? <><span className="spinner" /> Checking...</> : 'Test WAF Rules'}
      </button>

      {result && (
        <div style={{ marginTop: '1rem' }}>
          <div className={`result-box ${result.blocked || result.action === 'ERROR' ? 'result-error' : 'result-success'}`}>
            {result.realBlock && (
              <div style={{ 
                display: 'inline-block',
                padding: '0.25rem 0.5rem',
                background: 'var(--cf-danger)',
                color: 'white',
                fontSize: '0.7rem',
                fontWeight: 700,
                borderRadius: '4px',
                marginBottom: '0.75rem',
                textTransform: 'uppercase',
              }}>
                VERIFIED EDGE WAF BLOCK
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <span style={{ 
                fontSize: '1.5rem', 
                width: '40px', 
                height: '40px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: result.blocked ? 'var(--cf-danger)' : 'var(--cf-success)',
                color: 'white',
                borderRadius: '8px',
              }}>
                {result.blocked || result.action === 'ERROR' ? '✗' : '✓'}
              </span>
              <div>
                <strong style={{ fontSize: '1rem' }}>
                  {result.realBlock ? 'HTTP 403 Forbidden' : `Action: ${result.action}`}
                </strong>
                <div style={{ fontSize: '0.85rem', color: 'var(--cf-muted)' }}>{result.message}</div>
              </div>
            </div>

            {result.realBlock && result.rayId && (
              <div style={{ 
                marginBottom: '0.75rem', 
                padding: '0.5rem 0.75rem', 
                background: 'rgba(0,0,0,0.1)', 
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
              }}>
                <strong>Cloudflare Ray ID:</strong> {result.rayId}
              </div>
            )}

            {result.triggeredRules.length > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--cf-muted)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                  Triggered Rules
                </div>
                {result.triggeredRules.map((r, i) => (
                  <div key={i} style={{ 
                    padding: '0.5rem 0.75rem', 
                    background: 'rgba(255,255,255,0.5)', 
                    borderRadius: '4px',
                    marginBottom: '0.5rem',
                    fontSize: '0.85rem',
                  }}>
                    <div style={{ fontWeight: 600 }}>{r.rule}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--cf-muted)' }}>
                      Category: {r.category} | Matched: <code>{r.matched}</code>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {result.realBlock && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', fontStyle: 'italic', color: 'var(--cf-muted)' }}>
                Cloudflare returned a verifiable block response before the request reached the test handler.
              </div>
            )}
            {!result.realBlock && result.blocked && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', fontStyle: 'italic', color: 'var(--cf-muted)' }}>
                This is an application-level pattern match, not proof that a managed WAF rule fired.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rate Limiting Demo (Real) ─────────────────────────────────────
function RateLimitDemo() {
  const [count, setCount] = useState(0);
  const [remaining, setRemaining] = useState(5);
  const [blocked, setBlocked] = useState(false);
  const [windowResetTime, setWindowResetTime] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const LIMIT = 5;
  const WINDOW = 10; // 10 second window

  // Countdown timer effect
  useEffect(() => {
    if (!windowResetTime) return;
    
    const tick = () => {
      const now = Math.floor(Date.now() / 1000);
      const timeLeft = Math.max(0, windowResetTime - now);
      setCountdown(timeLeft);
      
      // Window expired - reset state
      if (timeLeft === 0) {
        setCount(0);
        setRemaining(LIMIT);
        setBlocked(false);
        setWindowResetTime(null);
      }
    };
    
    tick(); // Initial tick
    const interval = setInterval(tick, 200); // Update frequently for smooth countdown
    return () => clearInterval(interval);
  }, [windowResetTime]);

  // Fetch initial status
  useEffect(() => {
    fetchJson<any>(`${WORKERS.securityDemo}/rate-limit-status`)
      .then((data: any) => {
        if (data.windowActive) {
          setCount(data.count || 0);
          setRemaining(data.remaining || LIMIT);
          setBlocked(data.blocked || false);
          // Calculate reset time
          const resetTime = Math.floor(Date.now() / 1000) + (data.windowResetsIn || 0);
          setWindowResetTime(resetTime);
        }
      })
      .catch(() => {});
  }, []);

  const fire = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${WORKERS.securityDemo}/rate-limit-test`, { method: 'POST' });
      if (res.status !== 429 && !res.ok) throw new Error(`Request failed with HTTP ${res.status}`);
      const data = await res.json() as any;
      setCount(data.requestCount);
      setRemaining(data.remaining);
      setBlocked(data.blocked);
      
      // Set window reset time
      const resetIn = data.windowResetsIn || data.retryAfter || WINDOW;
      setWindowResetTime(Math.floor(Date.now() / 1000) + resetIn);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Rate-limit request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        Application-level rate limiting: <strong>5 requests per 10 seconds</strong>. A Worker tracks the counter
        per IP in KV and returns a real HTTP 429 with a Retry-After header when the limit is exceeded.
      </p>

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1.25rem' }}>
        <button className="btn btn-primary" onClick={fire} disabled={loading || (blocked && countdown > 0)}>
          {loading ? <span className="spinner" /> : 'POST /api/login'}
        </button>
        <span style={{ fontSize: '0.85rem', color: 'var(--cf-muted)' }}>
          {count}/{LIMIT} requests
        </span>
        {countdown > 0 && (
          <span style={{ 
            fontSize: '0.85rem', 
            fontWeight: 700,
            color: blocked ? 'var(--cf-danger)' : 'var(--cf-warning)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
          }}>
            <span style={{ 
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: blocked ? 'var(--cf-danger)' : 'var(--cf-warning)',
              animation: 'pulse 1s infinite',
            }} />
            Window resets in {countdown}s
          </span>
        )}
      </div>

      {/* Visual progress bar */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '1rem' }}>
        {Array.from({ length: LIMIT + 2 }).map((_, i) => (
          <div key={i} style={{
            flex: 1, height: '10px', borderRadius: '5px',
            background: i < count
              ? (i < LIMIT ? 'var(--cf-success)' : 'var(--cf-danger)')
              : 'var(--cf-border)',
            transition: 'background 0.2s',
          }} />
        ))}
      </div>

      {blocked ? (
        <div className="result-box result-error">
          <strong>HTTP 429 Too Many Requests</strong>
          <pre style={{ marginTop: '0.5rem', fontSize: '0.8rem', background: 'rgba(0,0,0,0.1)', padding: '0.5rem', borderRadius: '4px' }}>
{`HTTP/2 429
retry-after: ${countdown}
x-ratelimit-limit: 5
x-ratelimit-remaining: 0`}
          </pre>
          <p style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
            Rate limit triggered! Request #{count} was blocked.
            {countdown > 0 ? (
              <strong style={{ color: 'var(--cf-danger)' }}> Wait {countdown} seconds to retry.</strong>
            ) : (
              <strong style={{ color: 'var(--cf-success)' }}> Window expired — you can try again!</strong>
            )}
          </p>
        </div>
      ) : count > 0 ? (
        <div className="result-box result-success">
          <strong>HTTP 200 OK</strong> — Request {count} of {LIMIT} allowed.
          <br />
          <span style={{ fontSize: '0.85rem', color: 'var(--cf-muted)' }}>
            {remaining} requests remaining in this window.
          </span>
          {count === LIMIT && <><br /><em style={{ color: 'var(--cf-warning)' }}>Next request will trigger rate limit!</em></>}
        </div>
      ) : (
        <div className="result-box result-info">
          Click "POST /api/login" to make requests. After 5 requests in 10 seconds, you'll hit the rate limit and must wait for the window to expire.
        </div>
      )}

      {error && <div className="result-box result-error">{error}</div>}

      <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'var(--cf-bg)', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem' }}>
        <strong>How it works:</strong> Each request is counted per IP in a 10-second fixed window. 
        Once you hit 5 requests, subsequent requests are blocked until the window expires. 
        This demonstrates application-level enforcement in a Worker; it is not a native Cloudflare Rate Limiting rule.
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────
export default function SecurityPage() {
  return (
    <main className="main">
      <div className="demo-page">
        <div className="demo-page-header">
          <div className="breadcrumb">
            <Link to="/">Portal</Link>
            <span className="breadcrumb-sep">/</span>
            <span>Security</span>
          </div>
          <h2>Security Demos</h2>
          <p>
            Security demonstrations using real Cloudflare request data, verified edge responses, and
            clearly labeled application-level controls.
          </p>
        </div>

        <div className="demo-list">
          <DemoWidget
            icon="🧩"
            title="Turnstile CAPTCHA"
            product="Turnstile"
            script="Click 'Human Flow' to see a real Turnstile widget. Then show 'Bot Challenge' to see what automated traffic sees. Key point: no puzzles for real users."
          >
            <TurnstileDemo />
          </DemoWidget>

          <DemoWidget
            icon="🤖"
            title="Bot Management — Your Request"
            product="Bot Management"
            script="This shows YOUR real bot score. Point at the score — high means Cloudflare thinks you're human. Show the fingerprint data: IP, ASN, and TLS version."
          >
            <BotScoreDemo />
          </DemoWidget>

          <DemoWidget
            icon="🛡️"
            title="WAF Pattern Inspector"
            product="Workers Prototype"
            script="Click an attack preset to demonstrate application-level pattern inspection. This is a prototype; it does not claim that a managed WAF rule fired."
          >
            <WAFDemo />
          </DemoWidget>

          <DemoWidget
            icon="🚦"
            title="Rate Limiting"
            product="Workers + KV"
            script="Click the button 6 times. The Worker allows five requests, then returns a real HTTP 429 with Retry-After. This is application-level enforcement, not a native rate-limiting rule."
          >
            <RateLimitDemo />
          </DemoWidget>
        </div>
      </div>
    </main>
  );
}
